/**
 * Getting one receipt file from the requester's disk into a draft row.
 *
 * Three steps, in this order, and the bytes never pass through the API:
 *   1. `receipts.uploadUrl` mints a presigned S3 PUT scoped to one object.
 *   2. The browser PUTs the file straight to that URL.
 *   3. `receipts.extract` reads the stored object server-side and returns rows.
 *
 * The prototype called the model directly from the browser with no API key,
 * which only ever worked inside the artifact sandbox. It also held every receipt
 * as base64 in memory, which is why it downscaled images unconditionally. Here
 * the downscale survives only as an upload-size optimisation: it runs when a
 * file exceeds the cap the presign signature is bound to, and not otherwise.
 */
import { isAllowedReceiptType, MAX_RECEIPT_BYTES, needsTranscodeForOcr } from '@gdec/shared';
import { api } from '@/api/client';

const MAX_IMAGE_WIDTH = 1400;
const START_QUALITY = 0.88;
const MIN_QUALITY = 0.5;
const MAX_SHRINK_ATTEMPTS = 7;

const MAX_MB = Math.round(MAX_RECEIPT_BYTES / 1024 / 1024);

export interface PreparedFile {
  blob: Blob;
  contentType: string;
  filename: string;
  /** True when the file had to be re-encoded smaller to fit the upload cap. */
  optimized: boolean;
  /** True when the file was converted to JPEG so it could be read at all. */
  converted: boolean;
}

interface UploadUrlResponse {
  url: string;
  key: string;
}

/** One receipt as `receipts.extract` returns it; every field may be absent. */
export interface ExtractedWireRow {
  date?: string;
  receipt?: string;
  company?: string;
  tin?: string;
  description?: string;
  /** Always the home-currency figure; 0 when it could not be determined. */
  amount?: number;
  /** ISO 4217 as printed on the receipt. Absent or 'PHP' means no conversion. */
  currency?: string;
  /** The figure as printed, in `currency`. */
  originalAmount?: number | null;
  /** The rate `amount` was reached at, or null for a peso receipt. */
  fxRate?: number | null;
  vatStatus?: string;
  /** The VAT breakdown as printed, when the receipt showed one. */
  printedNet?: number | null;
  printedVat?: number | null;
  imageKey?: string;
}

export interface DateOptions {
  original: string;
  swapped: string;
  raw: string;
}

export interface AmbiguousWireRow extends ExtractedWireRow {
  dateOptions: DateOptions;
}

export interface FlaggedWireRow extends ExtractedWireRow {
  anomalies: Array<{ kind: string; message: string }>;
}

export interface ExtractResponse {
  accepted: ExtractedWireRow[];
  needsConfirmation: AmbiguousWireRow[];
  /**
   * Receipts the server could read but found a problem with. They are offered
   * to the requester rather than discarded — see the anomaly model in
   * @gdec/shared.
   */
  flagged: FlaggedWireRow[];
}

function renderToBlob(
  bitmap: ImageBitmap,
  width: number,
  height: number,
  quality: number,
): Promise<Blob | null> {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.resolve(null);
  // A white ground matters: a transparent PNG receipt would otherwise flatten
  // to black in JPEG and become unreadable.
  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(bitmap, 0, 0, width, height);
  return new Promise((resolve) => {
    canvas.toBlob((blob) => resolve(blob), 'image/jpeg', quality);
  });
}

/**
 * Re-encode an image as JPEG, shrinking it only as far as the upload cap
 * demands, and trading resolution for size but keeping the merchant, date and
 * amount legible.
 *
 * Two callers, two reasons: an oversized file has to come under the cap, and a
 * HEIC file has to become a format the reader accepts at all. A file that is
 * already small enough leaves on the first pass at full resolution.
 *
 * `shrank` says whether anything was given up to get there, so the caller can
 * tell the requester their receipt was reduced — a conversion that kept full
 * resolution is not worth mentioning.
 *
 * Returns null when the browser cannot decode the format or no attempt got
 * under the cap.
 */
async function toJpeg(file: File): Promise<{ blob: Blob; shrank: boolean } | null> {
  if (!file.type.startsWith('image/')) return null;

  let bitmap: ImageBitmap;
  try {
    bitmap = await createImageBitmap(file);
  } catch {
    return null;
  }

  try {
    let width = bitmap.width;
    let height = bitmap.height;
    let quality = START_QUALITY;
    let shrank = false;

    if (width > MAX_IMAGE_WIDTH) {
      height = Math.round(height * (MAX_IMAGE_WIDTH / width));
      width = MAX_IMAGE_WIDTH;
      shrank = true;
    }

    for (let attempt = 0; attempt < MAX_SHRINK_ATTEMPTS; attempt += 1) {
      const blob = await renderToBlob(bitmap, width, height, quality);
      if (blob && blob.size <= MAX_RECEIPT_BYTES) return { blob, shrank };
      quality = Math.max(MIN_QUALITY, quality - 0.12);
      width = Math.max(1, Math.round(width * 0.85));
      height = Math.max(1, Math.round(height * 0.85));
      shrank = true;
    }
    return null;
  } finally {
    bitmap.close();
  }
}

function asJpegName(filename: string): string {
  return filename.replace(/\.[a-z0-9]{1,8}$/i, '') + '.jpg';
}

/**
 * Reject unusable files early, convert what the reader cannot read, and shrink
 * oversized images rather than fail.
 *
 * The conversion runs before the size check on purpose. A HEIC photo off an
 * iPhone is usually well under the cap, so it used to be uploaded untouched and
 * then failed at the far end of the pipeline, several seconds later, with a
 * bare "OCR service returned 400" pinned to the row. Converting here means the
 * only artefact that ever reaches storage is one the reader accepts.
 */
export async function prepareFile(file: File): Promise<PreparedFile> {
  const contentType = file.type;
  if (!contentType || !isAllowedReceiptType(contentType)) {
    throw new Error('that file type is not accepted as a receipt');
  }
  if (file.size === 0) {
    throw new Error('that file is empty');
  }

  if (needsTranscodeForOcr(contentType)) {
    const jpeg = await toJpeg(file);
    // Not every browser can decode HEIC — Safari can, most others cannot. When
    // it cannot, say which file and what to do about it, rather than letting
    // the failure surface later as a number from an API the requester has no
    // reason to know exists.
    if (!jpeg) {
      throw new Error(
        'this browser cannot convert an iPhone HEIC photo — re-save it as JPEG, or set the iPhone camera to "Most Compatible", and upload again',
      );
    }
    return {
      blob: jpeg.blob,
      contentType: 'image/jpeg',
      filename: asJpegName(file.name),
      optimized: jpeg.shrank,
      converted: true,
    };
  }

  if (file.size <= MAX_RECEIPT_BYTES) {
    return { blob: file, contentType, filename: file.name, optimized: false, converted: false };
  }

  const smaller = await toJpeg(file);
  if (!smaller) {
    throw new Error(`that file is larger than ${MAX_MB}MB and could not be reduced`);
  }
  return {
    blob: smaller.blob,
    contentType: 'image/jpeg',
    filename: asJpegName(file.name),
    optimized: true,
    converted: contentType !== 'image/jpeg',
  };
}

/** PUT the bytes at the presigned URL and return the object key. */
export async function uploadReceipt(prepared: PreparedFile, period: string): Promise<string> {
  const { url, key } = await api.receipts.uploadUrl<UploadUrlResponse>({
    filename: prepared.filename,
    contentType: prepared.contentType,
    contentLength: prepared.blob.size,
    period,
  });

  let response: Response;
  try {
    // Plain fetch, not the API client: this request goes to S3, not to /rpc,
    // and must not carry the session token.
    response = await fetch(url, {
      method: 'PUT',
      headers: { 'Content-Type': prepared.contentType },
      body: prepared.blob,
    });
  } catch {
    throw new Error('the upload could not reach storage');
  }
  if (!response.ok) {
    throw new Error(`storage rejected the upload (${response.status})`);
  }
  return key;
}

interface QueuedJob {
  jobId: string;
  pollAfterMs: number;
}

type PollResponse = { ready: false } | ({ ready: true } & ExtractResponse);

/** How long to keep polling before giving up and letting the user type it in. */
const OCR_TIMEOUT_MS = 60_000;

/**
 * Read a receipt, via the egress function.
 *
 * The model call happens outside the VPC — this API has no route to the
 * internet — so extraction is queued and polled rather than answered inline.
 * The wait is normally a few seconds.
 *
 * Timing out is not an error the user needs to reason about: the caller falls
 * back to a blank row for manual entry either way, which is what the prototype
 * did when its own extraction failed.
 */
export async function extractReceipts(key: string, period: string): Promise<ExtractResponse> {
  const job = await api.receipts.extract<QueuedJob>({ key, period });

  const deadline = Date.now() + OCR_TIMEOUT_MS;
  let wait = job.pollAfterMs || 2000;

  while (Date.now() < deadline) {
    await sleep(wait);
    const result = await api.receipts.extractResult<PollResponse>({ jobId: job.jobId, period });
    if (result.ready) {
      const { ready: _ready, ...rows } = result;
      return rows;
    }
    // Back off gently: most jobs land on the first or second poll, and a slow
    // one should not hammer the API for a minute.
    wait = Math.min(Math.round(wait * 1.5), 8000);
  }

  throw new Error('reading the receipt took too long');
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
